import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../../config';
import GetLastBackup from './GetLastBackup';
import { useNavigate } from 'react-router-dom';


type BackupType = {
  fileName: string;
  generatedAt: string;
  uploaded: boolean;
  [key: string]: any;
};

type AllDataType = {
  backups: BackupType[]; // Assuming BackupType is another type you've defined
};

const Backup = () => {
  const { t }: { t: (key: string, options?: any) => string } = useTranslation();
  const navigate = useNavigate();
  
  const [data, setData] = useState<AllDataType>({backups: []});
  const [progress, setProgress] = useState(0);
  const [backupPath, setBackupPath] = useState<string | null>(null);
  const [lastBackupInfo, setLastBackupInfo] = useState<any>(null);

  useEffect(() => {
    fetchLastBackup();
  }, [navigate]); // Empty dependency array means this effect runs once on mount


  const fetchLastBackup = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/backup/last`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        }
      });
      const info = await response.json();
      if (info.success) {
        setLastBackupInfo(info.backup);
      } else {
        console.warn('⚠️ No backup info found');
      }
      const configuration = {
        url: `${API_BASE_URL}/admin/backups`,
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        },
      }

      axios.request<AllDataType>(configuration).then(
        (res) => {
          console.log('✅ Backups fetched successfully:', res.data.backups);
          setData({backups: res.data.backups});
        }
        ).catch((err) => {
          console.error('❌ Error fetching backups:', err);
          // navigate('/login');
      });

    } catch (err) {
      console.error('❌ Error fetching last backup info:', err);
    }
  };

  
  const simulateProgress = (setValue: (v: number) => void, onFinish: () => void) => {
    let value = 0;
    const interval = setInterval(() => {
      value += Math.random() * 20;
      if (value >= 100) {
        value = 100;
        clearInterval(interval);
        onFinish();
      }
      setValue(value);
    }, 300);
  };

  const handleGenerateBackup = async () => {
    setProgress(0);

    simulateProgress(setProgress, async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/backup/generate`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
          },
        });

        const data = await response.json();

        if (data.success) {
          setBackupPath(data.backupPath);
          fetchLastBackup();
        } else {
          alert(`⚠️ ${data.message}`);
        }
      } catch (error) {
        alert(`⚠️ ${error}`);
      }
    });
  };

  const handleDeleteBackup = async (fileId: string) => {
    if (window.confirm(t('admin.confirm_delete_backup'))) {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/backup/${fileId}/delete`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          fetchLastBackup();
          alert(t('admin.backup_deleted_successfully'));
        } else {
          alert(`⚠️ ${data.message}`);
        }
      } catch (error) {
        console.error('❌ Error deleting backup:', error);
        alert(`⚠️ ${error}`);
      }
    }
  };

  return (
    <div className="w-full flex flex-col md:flex-row justify-between px-5 py-5 rounded-lg my-10 gap-4">
      <h2 style={{ fontSize: '1.5rem', marginBottom: '20px' }}>{t('admin.backup_management')}</h2>
      <div className="md:w-1/2 flex justify-end">
        <div className="w-full max-w-[500px] rounded-lg p-5">
          <GetLastBackup info={lastBackupInfo} />
        </div>
      </div>
      <button
        onClick={handleGenerateBackup}
        disabled={progress > 0 && progress < 100}
        style={{
          padding: '10px 20px',
          marginBottom: '10px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: progress > 0 && progress < 100 ? 'not-allowed' : 'pointer',
          width: 'fit-content'
        }}
      >
        🗄️ {progress > 0 && progress < 100 ? `${t('admin.generating_backup')}... ${Math.round(progress)}%` : t('admin.generate_backup')}
      </button>

      {backupPath && (
        <div style={{ fontSize: '0.9rem', marginTop: '10px' }}>
          ${t('admin.last_backup_generated')}: <code style={{ wordBreak: 'break-all' }}>{backupPath}</code>
        </div>
      )}

      {progress > 0 && (
        <div style={{ marginTop: '10px', height: '10px', backgroundColor: '#e9ecef', borderRadius: '5px' }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#007bff',
            borderRadius: '5px',
            transition: 'width 0.3s'
          }}></div>
        </div>
      )}

      <div className="container mx-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3">
                {t('dashboard.title')}
              </th>
              <th>
                {t('admin.generated_at')}
              </th>
              <th>
                {t('admin.uploaded')}
              </th>
              <th>
                {t('admin.download')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.backups?.map(backup => 
            (
              <tr
              className='even:bg-cian-100 odd:bg-gray-100'
                key={backup._id}
              >
                <th scope="row" className="px-6 py-4 font-medium text-gray-900 dark:text-black whitespace-nowrap">
                  {backup.name.slice(0, 30)}
                </th>
                <th scope="row" className="px-6 py-4 font-medium text-gray-900 dark:text-black whitespace-nowrap">
                {backup.uploadedAt ? new Date(backup.uploadedAt).toLocaleString() : 'No Data'}
                </th>
                
                <th scope="row" className="px-6 py-4 font-medium text-gray-900 dark:text-black whitespace-nowrap">
                  {backup.uploadedAt ? `✅ ${t('answer.yes')}` : `❌ ${t('answer.no')}`}
                </th>
                <th>
                  {backup.googleDriveFileId ? (
                    <a
                      href={`${API_BASE_URL}/admin/backup/${backup.googleDriveFileId}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      {t('admin.download')}
                    </a>    
                    ) :
                    null
                  }
                </th>
                <th>
                  {backup.googleDriveFileId ? (

                    <button
                      type="button"
                      className="btn btn-primary btn-danger"
                      onClick={() => handleDeleteBackup(backup.googleDriveFileId)}
                    >
                      {t('admin.delete_backup')}
                    </button>
                    ) :
                    null
                  }

                </th>
              </tr>
            ))}
          </tbody>
        </table>    
      </div>
    </div>
  );
};

export default Backup;

